import { MethodType } from "./auth";
import { BaseModel, OrgOwnedModel } from "../models/BaseModel";
import { User } from "../models/User";
import { execute } from "./server";
import { getCollectionName } from "../models/ModelNavigator";
import { ArrayBufferCache } from "./ArrayBufferCache";
import { getCreds } from "../contexts/PageContext";

interface DeleteResult {
  acknowledged: boolean;
  deletedCount: number;
}

interface DeleteResponse {
  result: DeleteResult;
}

export async function loadRecord<T extends BaseModel>(
  collection: string,
  id: string
): Promise<T | undefined> {
  const res = await execute(MethodType.Get, "load_record", {
    collection,
    id,
  });
  if (res.error) {
    return undefined;
  }
  return res;
}

export async function loadRecords<T extends BaseModel>(
  collection: string,
  ...criteria: any[]
): Promise<T[]> {

  let criteriaObj: { [key: string]: any } = {};
  if (criteria.length === 1) {
    criteriaObj = criteria[0];
  } else {
    for (let i = 0; i < criteria.length; i += 2) {
      criteriaObj[criteria[i]] = criteria[i + 1];
    }
  }
  const res = await execute(
    MethodType.Post,
    "load_records",
    {
      collection,
    },
    criteriaObj
  );
  if (res.error) {
    return [];
  }
  return res as T[];
}

export async function saveModel<T extends BaseModel>(model: T): Promise<void> {
  const collection = getCollectionName(model);
  const res = await saveRecord(collection, model);
  const error = (res as any).error;
  if (error) {
    throw new Error(`Failed to save model: ${model._id} because of ${error}`);
  }
  Object.assign(model, res);
}

export async function deleteModel<T extends BaseModel>(
  model: T
): Promise<DeleteResponse> {
  const collection = getCollectionName(model);
  const res = await deleteRecord(collection, model);
  return res;
}

export async function loadModel<T extends BaseModel>(model: T): Promise<void> {
  const collection = getCollectionName(model);
  const res = await loadRecord(collection, model._id);
  if (res) {
    // console.log({ beforeAssign: model })
    for (const key in model) {
      if ((res as any)[key] !== undefined) {
        model[key] = (res as any)[key];
      }
    }
    // Object.assign(model, res);
    // console.log({ afterAssign: model });
  } else {
    throw new Error(`Failed to load model: ${model._id}`);
  }
}

export async function loadOrgModel<T extends OrgOwnedModel>(model: T): Promise<T> {
  const collection = getCollectionName(model);
  const res = await loadOrgRecord(collection, model._id);
  // if (res) {
  //   Object.assign(model, res);
  // } else {
  //   throw new Error(`Failed to load model: ${model._id}`);
  // }
  if (res) {
    for (const key in model) {
      if ((res as any)[key] !== undefined) {
        model[key] = (res as any)[key];
      }
    }
  } else {
    throw new Error(`Failed to load model: ${model._id}`);
  }
  return model;
}

export async function loadOrgRecord<T extends OrgOwnedModel>(collectionName: string, id: string): Promise<T | undefined> {
  const { user } = getCreds();
  if (!user) {
    throw new Error("User not found");
  }
  const criteria = ["orgId", user.orgId, "_id", id];

  const res = await loadRecords<T>(collectionName, ...criteria);

  if (res.length === 0) {
    return undefined;
  }

  return res[0];
}

export async function loadOrgRecords<T extends OrgOwnedModel>(collectionName: string, builder?: () => T): Promise<T[]> {
  const { user } = getCreds();
  if (!user) {
    throw new Error("User not found");
  }
  const criteria = ["orgId", user.orgId];

  const res = await loadRecords<T>(collectionName, ...criteria);
  if (builder) {
    const models: T[] = [];
    for (const item of res) {
      const model = builder();
      for (const key in model) {
        if ((item as any)[key] !== undefined) {
          model[key] = (item as any)[key];
        }
      }
      models.push(model);
    }
    return models;
  }
  return res as T[];
}

export async function saveRecord<T extends BaseModel>(
  collection: string,
  record: T
): Promise<T> {
  const res = await execute(
    MethodType.Post,
    "save_record",
    {
      collection,
    },
    record
  );
  return res;
}

export async function saveUser(record: User): Promise<User> {
  const res = await execute(MethodType.Post, "save_user", {}, record);
  return res;
}

export async function deleteRecord<T extends BaseModel>(
  collection: string,
  record: T
): Promise<DeleteResponse> {
  const res = await execute(MethodType.Delete, "delete_record", {
    collection,
    id: record._id,
  });
  return res;
}

export async function deleteUser(
  record: User
): Promise<DeleteResponse> {
  const res = await execute(MethodType.Delete, "delete_user", {
    id: record._id,
  });
  return res;
}

export async function indexDb() {
  const res = await execute(MethodType.Post, "honey-badger/index_db", {});
  return res;
}

export async function getSecret(secretName: string): Promise<string> {
  const res = await execute(MethodType.Get, "get_kv_value", {
    secretName,
  });
  return res.value;
}

const arrayBufferCache = new ArrayBufferCache();

export async function uploadBlob(containerName: string, blobName: string, data: ArrayBufferLike) {
  const cached = await arrayBufferCache.get(`${containerName}/${blobName}`);
  if (cached) {
    await arrayBufferCache.delete(`${containerName}/${blobName}`);
  }
  const res = await execute(MethodType.Post, "blob_upload", {
    containerName,
    blobName,
  }, data);
  return res;
}

export async function downloadBlob(containerName: string, blobName: string, cacheTtl = 0): Promise<ArrayBuffer> {
  const key = `${containerName}/${blobName}`;
  if (cacheTtl > 1) {
    const cached = await arrayBufferCache.get(key);
    if (cached) {
      return cached;
    }
  }
  // create empty ArrayBuffer
  const data = new ArrayBuffer(0);
  const res = await execute(MethodType.Get, "blob_download", {
    containerName,
    blobName,
  }, data);
  if (!res.error) {
    if (cacheTtl > 0) {
      await arrayBufferCache.set(key, res, cacheTtl);
    }
  }
  return res;
}

export async function deleteBlob(containerName: string, blobName: string): Promise<any> {
  const key = `${containerName}/${blobName}`;
  await arrayBufferCache.delete(key);
  const res = await execute(MethodType.Delete, "blob_delete", {
    containerName,
    blobName,
  });
  return res;
}

export async function helloWorld() {
  const res = await execute(MethodType.Get, "httpTrigger1");
  return res;
}

let _history = null;
let _navToken: any = {};
export function direct(route: string, token = {}) {
  _navToken = token;
  _history.push(route);
}

export function getNavToken() {
  return _navToken;
}
